<template>
	<div class="header-brand-section d-flex">
		<component
			:is="logoContainerComponent"
			:aria-label="themeAmelipro ? locales.homeLinkLabelAmelipro : locales.homeLinkLabel"
			class="w-100 d-flex align-center header-home-link"
			:href="homeHref"
			:to="homeHref=== undefined ? homeLink : undefined"
		>
			<AmeliproLogoAm
				:hide-signature="hideSignature"
				:size="logoSize"
			/>

			<slot>
				<svg
					v-if="showDivider"
					aria-hidden="true"
					class="header-home-link__divider"
					:fill="dividerColor"
					focusable="false"
					:height="dividerDimensions.height"
					role="img"
					viewBox="0 0 22 64"
					:width="dividerDimensions.width"
					xmlns="http://www.w3.org/2000/svg"
				>
					<path d="M14.3 49.3c-.2 0-.4-.2-.4-.4V14.2c0-.2.2-.4.4-.4.3 0 .5.2.5.4v34.7c0 .2-.2.4-.5.4Z" />
				</svg>

				<img
					v-if="ameliproLogo"
					:alt="ameliproLogo.alt"
					class="logo-amelipro"
					:src="ameliproLogo.src"
				>

				<span
					v-else-if="serviceTitle || serviceSubTitle"
					class="d-flex justify-center flex-column primary--text"
				>
					<h1
						v-if="serviceTitle"
						class="header-title font-weight-bold"
					>
						{{ serviceTitle }}
					</h1>

					<h2
						v-if="showServiceSubTitle"
						class="header-title"
					>
						{{ serviceSubTitle }}
					</h2>
				</span>
			</slot>
		</component>
	</div>
</template>

<script setup lang="ts">
	import { PropType, computed, useSlots } from 'vue';
	import BreakpointHelper from '../../../helpers/BreakpointHelper';
	import { IndexedObject } from '../../../types';
	import { LogoInfo } from './types';
	import { LogoSizeEnum } from '../../AmeliproLogoAm/LogoSizeEnum';
	import { RawLocation } from 'vue-router';
	import { convertToHex } from '../../../functions/convertToHex';
	import { dividerDimensionsMapping } from './dividerDimensionsMapping';
	import { locales } from './locales';

	const props = defineProps({
		homeHref: {
			type: String,
			default: undefined,
		},
		homeLink: {
			type: [String, Boolean, Object] as PropType<RawLocation>,
			default: '/',
		},
		mobileVersion: {
			type: Boolean,
			default: false,
		},
		serviceSubTitle: {
			type: String,
			default: undefined,
		},
		serviceTitle: {
			type: String,
			default: undefined,
		},
		themeAmelipro: {
			type: Boolean,
			default: true,
		},
	});

	const breakpointHelper = new BreakpointHelper();

	const slots = useSlots();

	const hideSignature = computed<boolean>(() => Boolean(slots.default));
	const ameliproLogo = computed<LogoInfo | undefined>(() => {
		const AmeliproLogoAmelipro = {
			src: require('../../../assets/img/amelipro.svg'),
			alt: locales.AmeliproLogoAmeliPro,
		};
		return props.themeAmelipro ? AmeliproLogoAmelipro : undefined;
	});

	const logoContainerComponent = computed<string>(() => {
		if (props.homeHref) {
			return 'a';
		}

		return 'RouterLink';
	});

	const showDivider = computed<boolean>(() => Boolean(ameliproLogo.value || props.serviceTitle));
	const showServiceSubTitle = computed<boolean>(() => Boolean(props.serviceTitle && props.serviceSubTitle));
	const dividerColor = computed<string>(() => (props.themeAmelipro ? '#006386' : convertToHex('ap-blue')));
	const dividerDimensions = computed<IndexedObject>(() => {
		const { xSmall, small, normal } = dividerDimensionsMapping;

		if (breakpointHelper.is('xsOnly')) {
			return xSmall;
		}

		if (props.mobileVersion) {
			return small;
		}

		return normal;
	});

	const logoSize = computed<LogoSizeEnum>(() => {
		if (breakpointHelper.is('xsOnly')) {
			return LogoSizeEnum.X_SMALL;
		}

		if (props.mobileVersion) {
			return LogoSizeEnum.SMALL;
		}

		return LogoSizeEnum.NORMAL;
	});

</script>

<style lang="scss" scoped>
	.header-brand-section {
		overflow: hidden;

		.header-title {
			line-height: 1.45 !important;
		}

		h1.header-title {
			font-family: 'cabin';
			font-size: $font-size-xxl !important;
			letter-spacing: -0.48px !important;

			@media #{$media-down-md} {
				font-size: $font-size-lg !important;
				letter-spacing: -0.4px !important;
			}

			@media #{$media-only-xs} {
				font-size: $font-size-md !important;
				letter-spacing: -0.32px !important;
			}
		}

		h2.header-title {
			font-size: $font-size-md !important;
			color: $ap-grey-darken1;

			@media #{$media-down-md} {
				font-size: $font-size-sm !important;
			}

			@media #{$media-only-xs} {
				font-size: $font-size-xs !important;
			}
		}

		:deep(img) {
			width: auto;
			height: 100%;
			flex: none;
		}

		.logo-amelipro {
			width: 98px;

			@media #{$media-down-md} {
				width: 77px;
			}

			@media #{$media-only-xs} {
				width: 52px;
			}
		}

		svg,
		.header-home-link {
			flex: none;
		}

		.header-home-link {
			text-decoration: none !important;
		}
	}
</style>
